import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useOutsideClick } from "../../hooks/useOutsideClick";
import AccountTab from "../../components/account/AccountTab";
import AccountTabModal from "../../components/account/AccountTabModal";
import SkeletonVideoCard from "../../components/skeleton/SkeletonVideoCard";
import { deleteDraft, getAllDrafts, resetLoadingMore } from "../../redux/ApiSlice/DraftSlice";
import AccountProfileSection from "../../components/account/AccountProfileSection";
import VideoCard from "../../components/common/VideoCard";

const DraftVideos = () => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [show, setShow] = useState(false);
    const [activeTab, setActiveTab] = useState("");
    const ref = useRef(null);
    const containerRef = useRef(null);

    const handleClickOutside = () => {
        setShow(false);
        setActiveTab("");
    };
    useOutsideClick(ref, handleClickOutside);


    const openDrawer = () => setIsDrawerOpen(true);
    const closeDrawer = () => setIsDrawerOpen(false);

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { draftVideos, loading, loadingMore, nextPage } = useSelector((state) => state.draft);
    const { User_profile } = useSelector((state) => state.AccountProfile);
    const [deletePostId, setDeletePostId] = useState(null);


    const handleDraftsScroll = useCallback(() => {
        const container = containerRef.current;
        if (!container || loadingMore || !nextPage || deletePostId) return;

        const { scrollTop, scrollHeight, clientHeight } = container;
        const threshold = 50;

        if (scrollHeight - scrollTop - clientHeight <= threshold) {
            dispatch(getAllDrafts({ nextUrl: nextPage, isLoadMore: true }));
        }
    }, [dispatch, loadingMore, nextPage, deletePostId]);

    useEffect(() => {
        dispatch(resetLoadingMore());
        dispatch(getAllDrafts({ nextUrl: null, isLoadMore: false }));
    }, [dispatch]);

    useEffect(() => {
        const container = containerRef.current;
        if (container) {
            container.addEventListener('scroll', handleDraftsScroll);
            return () => container.removeEventListener('scroll', handleDraftsScroll);
        }
    }, [handleDraftsScroll]);



    const handleDeleteClick = (id) => {
        setDeletePostId(id);
    };

    const handleConfirmDelete = async () => {
        if (deletePostId) {
            await dispatch(deleteDraft(deletePostId)).unwrap();
            setDeletePostId(null);
        }
    };

    const handleCancelDelete = () => {
        setDeletePostId(null);
    };

    const handleNavigateToDraft = (id) => {
        navigate(`/draft/${id}`);
    };

    return (
        <>
            <div
                className={`bg-[#fff] dark:bg-[#1E1D21] h-full relative ${isDrawerOpen ? "overflow-hidden" : "overflow-y-scroll"}`}
                ref={containerRef}
            >
                <AccountProfileSection
                    handleOpenSideBar={openDrawer}
                    handleCloseSideBar={closeDrawer}
                    isDrawerOpen={isDrawerOpen}
                    setActiveTab={setActiveTab}
                    setShow={setShow}
                    show={show}
                />

                <div className="sticky top-0 z-30">
                    <AccountTab activeTab={"Drafts"} />
                </div>

                {draftVideos?.results?.total_draft_count > 0 &&
                    <p className="text-[10px] text-end font-semibold px-2 text-gray-500 dark:text-gray-700">
                        {draftVideos?.results?.total_draft_count}
                        {draftVideos?.results?.total_draft_count === 1 ? " Draft" : " Drafts"}
                    </p>
                }


                <div className="w-full">
                    {loading ? (
                        <SkeletonVideoCard />
                    ) : (
                        <div>
                            {draftVideos?.results?.drafts?.length > 0 ? (
                                <div className="grid grid-cols-3 gap-1 px-2 pb-2 w-full h-full">
                                    {draftVideos.results.drafts.map((video) => (
                                        <VideoCard
                                            key={video.id}
                                            id={video.id}
                                            hideSound={false}
                                            body={video.post_description}
                                            belowDecs={true}
                                            threeDots={false}
                                            video={video.video_url}
                                            likes_count={0}
                                            view_count={0}
                                            authorId={User_profile?.id}
                                            selectVideo={handleNavigateToDraft}
                                            onDeleteClick={handleDeleteClick}
                                            showDelete={true}
                                            thumbnail={video.thumbnail_url}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div className="flex items-center justify-center">
                                    <p className="text-[12px] mt-20 text-gray-600 dark:text-gray-700">
                                        Your drafts will appear here.
                                    </p>
                                </div>
                            )}

                            {/* Loading more indicator */}
                            {loadingMore && (
                                <div className="flex justify-center items-center py-4">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {show && (
                <div className={"absolute h-full w-full top-0 overflow-hidden"}>
                    <div
                        className="absolute h-screen w-full top-0 opacity-100 bg-opacity-70 bg-black z-50"
                        onClick={handleClickOutside}
                    />
                    <div
                        ref={ref}
                        className="absolute top-0 left-4 z-50 w-[90%] h-[-webkit-fill-available] mt-4 mb-3 flex justify-center rounded-lg bg-black duration-1000 transform opacity-100"
                    >
                        <AccountTabModal
                            ref={ref}
                            activeTab={activeTab}
                            setActiveTab={setActiveTab}
                        />
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal - Positioned outside scrollable container */}
            {deletePostId && (
                <div
                    className="absolute top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[200]"
                    onClick={handleCancelDelete}
                >
                    <div
                        className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-sm mx-4"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <p className="text-[24px] font-bold text-black dark:text-white text-center">
                            Delete Draft?
                        </p>
                        <p className="text-[12px] text-black mb-2 dark:text-white text-center">
                            Are you sure you want to delete this draft?
                        </p>
                        <div className="flex gap-3 mt-4 justify-center">
                            <button
                                onClick={handleCancelDelete}
                                className="px-2 py-1 rounded-md bg-[#1b1b1b] dark:bg-gray-400 dark:text-black dark:hover:bg-gray-500 hover:bg-[#0f0f0f] text-white"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmDelete}
                                className="px-2 py-1 rounded-md border border-gray-400 dark:border-gray-800 bg-red-900 hover:bg-red-600 text-white"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default DraftVideos